import { lazy, type ComponentType, type LazyExoticComponent, type ReactNode } from 'react'
import type { CrmPage } from '../crm/crmBridge'
import type { CrmPermissions } from '../crm/crmRail'

/** What every Fansly page gets from the account CRM route (AccountCrm). */
export interface FanslyPageProps {
  accountId: string
  /** The page asked for (pages that serve several, e.g. Statistics tabs, read it). */
  page: CrmPage
  perms: CrmPermissions | null
  canRead: (page: CrmPage) => boolean
  canWrite: (page: CrmPage) => boolean
  /** The page's URL query, without the "?" (e.g. Collections' `view=followers` from the Fansly menu). */
  query?: string
  /**
   * Opens a CRM page of this account (`query` without the "?"). With `replace`
   * the history entry is swapped: a page keeping its own view in the URL.
   */
  onOpenPage: (page: CrmPage, query?: string, opts?: { replace?: boolean }) => void
  /** Opens the fan's conversation on Chats (Fansly account id of the fan). */
  onOpenChat: (fanId: string) => void
  /** Chats reports the account's unread conversations for the rail badge. */
  reportUnread: (count: number) => void
  /** The phone's bottom tab bar (the CRM pages). */
  mobileNav?: ReactNode
}

type Page = LazyExoticComponent<ComponentType<FanslyPageProps>>

/**
 * The Fansly pages built in React (ADR 0036): a page listed here renders in
 * the console for a Fansly account; any other page still shows the embedded
 * Fansly workspace. Each section uncomments its own line(s).
 */
export const FANSLY_PAGES: Partial<Record<CrmPage, Page>> = {
  more: lazy(() => import('./more/MorePage')),

  home: lazy(() => import('./home/HomePage')),

  notifications: lazy(() => import('./notifications/NotificationsPage')),

  profile: lazy(() => import('./profile/ProfilePage')),

  chats: lazy(() => import('./chats/ChatsPage')),

  collections: lazy(() => import('./collections/CollectionsPage')),

  vault: lazy(() => import('./vault/VaultPage')),

  queue: lazy(() => import('./queue/QueuePage')),

  statements: lazy(() => import('./wallet/WalletPage')),

  statistics: lazy(() => import('./statistics/StatisticsPage')),

  tracking: lazy(() => import('./tracking/TrackingPage')),

  plans: lazy(() => import('./plans/PlansPage')),

  messaging: lazy(() => import('./messaging/MessagingPage')),

  management: lazy(() => import('./management/ManagementPage')),

  streaming: lazy(() => import('./streaming/StreamingPage')),

  settings: lazy(() => import('./settings/SettingsPage')),
}

export function hasFanslyPage(page: string): boolean {
  return Object.prototype.hasOwnProperty.call(FANSLY_PAGES, page)
}
